import { UIState, LeftHandPanel } from 'js/types';
import { getProjectViewModeInitialState } from 'js/sagas/sagaHelpers/projectViewMode';
import { getTimelineOpenStatus } from 'js/sagas/sagaHelpers/timelineLocalStorage';
import {
  NEEDS_TO_ACCEPT_TERMS,
  NEEDS_TO_ACCEPT_TERMS_ERROR,
  NeedsToAcceptTermsAction,
  NeedsToAcceptTermsErrorAction,
  USER_ACCEPTS_TERMS,
  USER_ACCEPTS_TERMS_FAILED,
  USER_ACCEPTS_TERMS_SUCCESS,
  UserAcceptsTermsAction,
  UserAcceptsTermsFailedAction,
  UserAcceptsTermsSuccessAction
} from 'js/actionCreators/acceptedTermsActions';
import {
  VALIDATE_TOKEN_FAILED,
  SET_TRIAL_HAS_EXPIRED,
  SET_NO_ONLINE_STORAGE,
  LOGOUT
} from 'js/actionCreators/authActions';
import {
  CLEAR_REPLACE_IMAGE,
  START_REPLACE_IMAGE,
  StartReplaceImageAction,
  ClearReplaceImageAction
} from 'js/actionCreators/imagesActions';
import { PLAY_FROM_EDITOR, PlayFromEditorAction } from 'js/actionCreators/playbackActions';
import { SHOW_IMAGE_GENERATION_VIEW, ShowImageGenerationViewAction } from 'js/actionCreators/ImageDrawerActions';

import {
  SHOW_CALLOUT,
  HIDE_CALLOUT,
  SHOW_ERROR,
  EDITOR_MODES,
  UNLOAD_PLAYBACK,
  LOAD_PLAYBACK,
  LOADED_PLAYBACK_ASSETS,
  UI_LOADING,
  CURRENT_HOME_VIEW,
  AUDIO_RECORDING_START,
  AUDIO_RECORDING_RESET,
  CURRENT_EDIT_ANIMATION_TAB,
  APPLICATION_FONTS_LOADED,
  SET_EDITOR_LOADING_MESSAGE,
  ShowCalloutAction,
  ShowErrorAction,
  UnloadPlaybackAction,
  LoadPlaybackAction,
  SetCurrentHomeViewAction,
  AudioRecordingStartAction,
  AudioRecordingCancelAction,
  SetCurrentEditAnimationTabAction,
  SetEditorLoadingMessageAction,
  StartLoadingAction,
  StopLoadingAction,
  PreviewLinkLoadingAction,
  SetProjectsViewModeAction,
  PREVIEW_LINK_LOADING,
  SET_PROJECTS_VIEW_MODE,
  ShowElementsPanelAction,
  SHOW_ELEMENTS_PANEL,
  SHOW_PASTE_WARNING,
  ShowPasteWarningAction,
  CLOSE_PASTE_WARNING,
  ClosePasteWarningAction,
  SHOW_ELEMENT_NOT_IN_VIEW_WARNING,
  ShowElementNotInViewWarningAction,
  CloseElementNotInViewWarningAction,
  CLOSE_ELEMENT_NOT_IN_VIEW_WARNING,
  SHOW_EXPIRED_SUBSCRIPTION_MODAL,
  ShowExpiredSubscriptionModalAction,
  SHOW_PAUSED_SUBSCRIPTION_MODAL,
  ShowPausedSubscriptionModalAction,
  HIDE_UPGRADE_WARNING_MODAL,
  HideUpgradeModalAction,
  SET_SPLIT_MODE,
  SetSplitModeAction,
  SHOW_PREMIUM_FEATURE_MODAL,
  ShowPremiumFeatureModalAction,
  CLOSE_PREMIUM_FEATURE_MODAL,
  ClosePremiumFeatureModalAction,
  DismissAudioLimitsModalAction,
  ShowAudioLimitsModalAction,
  DISMISS_AUDIO_LIMITS_MODAL,
  SHOW_AUDIO_LIMITS_MODAL,
  SHOW_LEFT_HAND_PANEL,
  ShowLeftHandPanelAction,
  CLOSE_LEFT_HAND_PANEL,
  CloseLeftHandPanelAction,
  TOGGLE_SMART_GUIDES,
  ToggleSmartGuidesAction,
  HideUpsellProfileBoxAction,
  HIDE_UPSELL_PROFILE_BOX
} from '../actionCreators/uiActions';
import { RESET_STATE } from '../actionCreators/generalActions';
import {
  EditorSetEditPanelModeAction,
  EDITOR_SET_EDIT_PANEL_MODE,
  TOGGLE_NEW_TIMELINE,
  EDITOR_CLOSED,
  TOGGLE_FIRST_ARROW_PRESS
} from '../actionCreators/editorActions';
import {
  PrepScribeForEditorSuccessAction,
  PREP_SCRIBE_FOR_EDITOR_SUCCESS,
  ToggleGridAction,
  TOGGLE_GRID,
  UPDATE_EDITOR_POSITION_ZOOM,
  UpdateEditorPositionAndZoomAction,
  TOGGLE_CANVAS_DRAG,
  ToggleCanvasDragAction,
  SET_SELECTED_AUDIO_CLIP,
  SetSelectedAudioClipAction,
  CREATE_TEXT_ELEMENT,
  CreateTextElementAction
} from '../actionCreators/scribeActions';
import {
  LOAD_IMAGE_CROP,
  CANCEL_IMAGE_CROP,
  CONFIRM_IMAGE_CROP,
  START_IMAGE_CROP,
  CONFIRM_IMAGE_CROP_SUCCESS,
  CONFIRM_IMAGE_CROP_FAILED,
  StartImageCropAction,
  LoadImageCropAction,
  ConfirmImageCropFailedAction
} from '../actionCreators/croppingActions';
import { SplitAudioTrackAction, SPLIT_AUDIO_TRACK } from '../actionCreators/audioActions';
import { UserSettingsModalAction, USER_SETTINGS_MODAL } from '../actionCreators/userSettingsActions';

import { setScrollToTime } from './advancedTimeline';
import { goBackToScript, startFromScript } from './voiceoversReducer';
import { startNewScript } from './projectScripts';

const initialState: UIState = {
  showCallout: null,
  editorMode: EDITOR_MODES.default,
  isLoadingPlayback: false,
  uiIsLoading: false,
  currentHomeView: 'my-projects',
  editPanelMode: null,
  isAudioPanelBusy: false,
  currentEditAnimationTab: null,
  applicationFontsLoaded: false,
  editorLoadingMessage: 'Loading...',
  previewLinkIsLoading: false,
  projectsViewMode: getProjectViewModeInitialState(),
  newTimeline: getTimelineOpenStatus(),
  showElementsPanel: true,
  firstArrowPressed: false,
  canvasDragOn: false,
  grid: false,
  showPasteWarning: false,
  isCheckingTermsAcceptance: true,
  needsToAcceptTerms: false,
  acceptingTerms: false,
  showElementNotInViewWarning: false,
  elementNotInViewProps: { link: '', onConfirm: undefined, onCancel: undefined },
  showUpgradeWarningModal: false,
  upgradeModalTitle: '',
  upgradeModalInfo: undefined,
  upgradeModalMessage: '',
  upgradeModalCta: '',
  audioSplitElement: null,
  showPremiumFeatureModal: false,
  premiumFeatureModalTitle: null,
  premiumFeatureModalContent: null,
  premiumFeatureTriggerSource: null,
  showAudioLimitModal: false,
  leftHandPanel: LeftHandPanel.SCENES,
  smartGuides: true,
  showUserSettingsModal: false,
  upsellProfileBoxIsHidden: false
};

type UIAction =
  | ShowImageGenerationViewAction
  | {
      type:
        | typeof HIDE_CALLOUT
        | typeof LOADED_PLAYBACK_ASSETS
        | typeof CANCEL_IMAGE_CROP
        | typeof CONFIRM_IMAGE_CROP
        | typeof CONFIRM_IMAGE_CROP_SUCCESS
        | typeof APPLICATION_FONTS_LOADED
        | typeof RESET_STATE
        | typeof TOGGLE_NEW_TIMELINE
        | typeof TOGGLE_FIRST_ARROW_PRESS
        | typeof EDITOR_CLOSED
        | typeof VALIDATE_TOKEN_FAILED
        | typeof SET_TRIAL_HAS_EXPIRED
        | typeof SET_NO_ONLINE_STORAGE
        | typeof HIDE_UPSELL_PROFILE_BOX
        | typeof LOGOUT;
    }
  | ShowCalloutAction
  | ShowErrorAction
  | UnloadPlaybackAction
  | LoadPlaybackAction
  | SetCurrentHomeViewAction
  | AudioRecordingStartAction
  | AudioRecordingCancelAction
  | StartImageCropAction
  | LoadImageCropAction
  | ConfirmImageCropFailedAction
  | SetCurrentEditAnimationTabAction
  | SetEditorLoadingMessageAction
  | PrepScribeForEditorSuccessAction
  | EditorSetEditPanelModeAction
  | StartLoadingAction
  | StopLoadingAction
  | PreviewLinkLoadingAction
  | SetProjectsViewModeAction
  | ShowElementsPanelAction
  | ToggleGridAction
  | ToggleCanvasDragAction
  | UpdateEditorPositionAndZoomAction
  | ShowPasteWarningAction
  | ClosePasteWarningAction
  | NeedsToAcceptTermsAction
  | UserAcceptsTermsAction
  | UserAcceptsTermsFailedAction
  | UserAcceptsTermsSuccessAction
  | NeedsToAcceptTermsErrorAction
  | ShowElementNotInViewWarningAction
  | CloseElementNotInViewWarningAction
  | ShowExpiredSubscriptionModalAction
  | ShowPausedSubscriptionModalAction
  | HideUpgradeModalAction
  | SetSplitModeAction
  | ShowPremiumFeatureModalAction
  | ClosePremiumFeatureModalAction
  | ShowAudioLimitsModalAction
  | DismissAudioLimitsModalAction
  | SetSelectedAudioClipAction
  | ShowLeftHandPanelAction
  | CloseLeftHandPanelAction
  | CreateTextElementAction
  | StartReplaceImageAction
  | ClearReplaceImageAction
  | PlayFromEditorAction
  | ToggleSmartGuidesAction
  | HideUpsellProfileBoxAction
  | UserSettingsModalAction
  | SplitAudioTrackAction
  | ReturnType<typeof setScrollToTime>
  | ReturnType<typeof goBackToScript>
  | ReturnType<typeof startNewScript>
  | ReturnType<typeof startFromScript>;

export default function uiReducers(state = initialState, action: UIAction): UIState {
  switch (action.type) {
    case SHOW_CALLOUT: {
      return {
        ...state,
        showCallout: action.callout
      };
    }

    case HIDE_CALLOUT: {
      return {
        ...state,
        showCallout: null
      };
    }

    case SHOW_ERROR: {
      return {
        ...state,
        showCallout: { title: action.error.message, details: action.error.description, isError: true }
      };
    }

    case RESET_STATE: {
      return {
        ...initialState
      };
    }

    case UNLOAD_PLAYBACK: {
      return {
        ...state,
        isLoadingPlayback: false
      };
    }

    case LOAD_PLAYBACK: {
      return {
        ...state,
        isLoadingPlayback: action.showLoading
      };
    }

    case LOADED_PLAYBACK_ASSETS: {
      return {
        ...state,
        isLoadingPlayback: false
      };
    }

    case UI_LOADING: {
      return {
        ...state,
        uiIsLoading: action.isLoading
      };
    }

    case CURRENT_HOME_VIEW: {
      return {
        ...state,
        currentHomeView: action.currentHomeView
      };
    }

    case EDITOR_SET_EDIT_PANEL_MODE: {
      return {
        ...state,
        editPanelMode: action.mode
      };
    }

    case UPDATE_EDITOR_POSITION_ZOOM: {
      let editorTransform = { ...state.editorTransform };
      if (!editorTransform) {
        editorTransform = { [action.sceneId]: action.vpTransform };
      } else {
        editorTransform[action.sceneId] = action.vpTransform;
      }

      return { ...state, editorTransform };
    }

    case AUDIO_RECORDING_START: {
      return {
        ...state,
        isAudioPanelBusy: true
      };
    }

    case AUDIO_RECORDING_RESET: {
      return {
        ...state,
        isAudioPanelBusy: false
      };
    }

    case START_IMAGE_CROP: {
      return {
        ...state,
        uiIsLoading: true
      };
    }

    case LOAD_IMAGE_CROP: {
      return {
        ...state,
        editorMode: EDITOR_MODES.IMAGE_CROP,
        uiIsLoading: false
      };
    }

    case CANCEL_IMAGE_CROP: {
      return {
        ...state,
        editorMode: EDITOR_MODES.default,
        uiIsLoading: false
      };
    }

    case CONFIRM_IMAGE_CROP: {
      return {
        ...state,
        uiIsLoading: true
      };
    }

    case CONFIRM_IMAGE_CROP_FAILED:
    case CONFIRM_IMAGE_CROP_SUCCESS: {
      return {
        ...state,
        editorMode: EDITOR_MODES.default,
        uiIsLoading: false
      };
    }

    case CURRENT_EDIT_ANIMATION_TAB: {
      return {
        ...state,
        currentEditAnimationTab: action.currentEditAnimationTab
      };
    }

    case APPLICATION_FONTS_LOADED: {
      return {
        ...state,
        applicationFontsLoaded: true
      };
    }

    case SET_EDITOR_LOADING_MESSAGE: {
      return {
        ...state,
        editorLoadingMessage: `${action.message}...`
      };
    }

    case PREP_SCRIBE_FOR_EDITOR_SUCCESS: {
      return {
        ...state,
        editorLoadingMessage: initialState.editorLoadingMessage
      };
    }

    case PREVIEW_LINK_LOADING: {
      return {
        ...state,
        previewLinkIsLoading: action.isLoading
      };
    }

    case SET_PROJECTS_VIEW_MODE: {
      return {
        ...state,
        projectsViewMode: action.mode
      };
    }

    case TOGGLE_NEW_TIMELINE: {
      return { ...state, newTimeline: !state.newTimeline };
    }

    case setScrollToTime.type: {
      return { ...state, newTimeline: true };
    }

    case TOGGLE_FIRST_ARROW_PRESS: {
      return { ...state, firstArrowPressed: !state.firstArrowPressed };
    }

    case SHOW_ELEMENTS_PANEL: {
      return {
        ...state,
        showElementsPanel: action.value
      };
    }

    case EDITOR_CLOSED: {
      return {
        ...state,
        showElementsPanel: initialState.showElementsPanel,
        leftHandPanel: initialState.leftHandPanel
      };
    }

    case TOGGLE_GRID: {
      return {
        ...state,
        grid: !state.grid
      };
    }

    case TOGGLE_CANVAS_DRAG: {
      return {
        ...state,
        canvasDragOn: !state.canvasDragOn
      };
    }

    case SHOW_PASTE_WARNING: {
      return {
        ...state,
        showPasteWarning: true
      };
    }

    case CLOSE_PASTE_WARNING: {
      return {
        ...state,
        showPasteWarning: false
      };
    }

    case SHOW_ELEMENT_NOT_IN_VIEW_WARNING: {
      return {
        ...state,
        showElementNotInViewWarning: true,
        elementNotInViewProps: { link: action.link, onConfirm: action.onConfirm, onCancel: action.onCancel }
      };
    }
    case CLOSE_ELEMENT_NOT_IN_VIEW_WARNING: {
      return {
        ...state,
        showElementNotInViewWarning: false
      };
    }

    case NEEDS_TO_ACCEPT_TERMS: {
      return {
        ...state,
        needsToAcceptTerms: action.needsToAccept,
        isCheckingTermsAcceptance: false
      };
    }

    case VALIDATE_TOKEN_FAILED:
    case SET_TRIAL_HAS_EXPIRED:
    case SET_NO_ONLINE_STORAGE:
    case LOGOUT: {
      return {
        ...state,
        isCheckingTermsAcceptance: false
      };
    }

    case NEEDS_TO_ACCEPT_TERMS_ERROR: {
      return {
        ...state,
        isCheckingTermsAcceptance: false,
        needsToAcceptTerms: false
      };
    }

    case USER_ACCEPTS_TERMS: {
      return {
        ...state,
        acceptingTerms: true
      };
    }

    case USER_ACCEPTS_TERMS_FAILED: {
      return {
        ...state,
        acceptingTerms: false
      };
    }

    case USER_ACCEPTS_TERMS_SUCCESS: {
      return {
        ...state,
        needsToAcceptTerms: false
      };
    }

    case SHOW_EXPIRED_SUBSCRIPTION_MODAL: {
      return {
        ...state,
        showUpgradeWarningModal: true,
        upgradeModalTitle: action.title,
        upgradeModalMessage: action.message,
        upgradeModalCta: action.ctaText
      };
    }

    case SHOW_PAUSED_SUBSCRIPTION_MODAL: {
      return {
        ...state,
        showUpgradeWarningModal: true,
        upgradeModalTitle: action.title,
        upgradeModalInfo: action.info,
        upgradeModalMessage: action.message,
        upgradeModalCta: action.ctaText
      };
    }

    case HIDE_UPGRADE_WARNING_MODAL: {
      return {
        ...state,
        showUpgradeWarningModal: false,
        upgradeModalTitle: '',
        upgradeModalInfo: '',
        upgradeModalMessage: '',
        upgradeModalCta: ''
      };
    }
    case SET_SPLIT_MODE: {
      return {
        ...state,
        newTimeline: true,
        audioSplitElement: action.modeOn === true ? action.id : null
      };
    }

    case SHOW_PREMIUM_FEATURE_MODAL: {
      return {
        ...state,
        showPremiumFeatureModal: true,
        premiumFeatureModalTitle: action.title,
        premiumFeatureModalContent: action.content,
        premiumFeatureTriggerSource: action.triggerSource
      };
    }

    case CLOSE_PREMIUM_FEATURE_MODAL: {
      return {
        ...state,
        showPremiumFeatureModal: false,
        premiumFeatureModalTitle: null,
        premiumFeatureModalContent: null,
        premiumFeatureTriggerSource: null
      };
    }

    case SHOW_AUDIO_LIMITS_MODAL: {
      return {
        ...state,
        showAudioLimitModal: [action.modalType, action.projectId]
      };
    }

    case DISMISS_AUDIO_LIMITS_MODAL: {
      return {
        ...state,
        showAudioLimitModal: false
      };
    }

    case SPLIT_AUDIO_TRACK:
    case SET_SELECTED_AUDIO_CLIP: {
      return {
        ...state,
        audioSplitElement: null
      };
    }

    case SHOW_LEFT_HAND_PANEL: {
      return {
        ...state,
        leftHandPanel: action.name
      };
    }

    case SHOW_IMAGE_GENERATION_VIEW: {
      return {
        ...state,
        leftHandPanel: LeftHandPanel.IMAGES
      };
    }

    case startFromScript.type: {
      return {
        ...state,
        leftHandPanel: LeftHandPanel.VOICEOVER
      };
    }

    case goBackToScript.type: {
      return {
        ...state,
        leftHandPanel: LeftHandPanel.SCRIPTS
      };
    }

    case startNewScript.type: {
      return {
        ...state,
        leftHandPanel: LeftHandPanel.SCRIPTS
      };
    }

    case CLOSE_LEFT_HAND_PANEL: {
      return {
        ...state,
        leftHandPanel: null
      };
    }

    case START_REPLACE_IMAGE: {
      return {
        ...state,
        leftHandPanel: LeftHandPanel.IMAGES
      };
    }

    case CLEAR_REPLACE_IMAGE: {
      return {
        ...state,
        leftHandPanel: null
      };
    }

    case CREATE_TEXT_ELEMENT: {
      if (state.leftHandPanel === LeftHandPanel.SCENES) {
        return state;
      }

      return {
        ...state,
        leftHandPanel: null
      };
    }

    case PLAY_FROM_EDITOR: {
      if (state.leftHandPanel === LeftHandPanel.SCENES) {
        return state;
      }

      return {
        ...state,
        leftHandPanel: null
      };
    }

    case TOGGLE_SMART_GUIDES: {
      return {
        ...state,
        smartGuides: !state.smartGuides
      };
    }

    case USER_SETTINGS_MODAL: {
      return {
        ...state,
        showUserSettingsModal: action.isOpen
      };
    }

    case HIDE_UPSELL_PROFILE_BOX: {
      return {
        ...state,
        upsellProfileBoxIsHidden: true
      };
    }

    default:
      return state;
  }
}
